import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Newspaper, ArrowUpRight, Mail } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export const NewsRadar = () => {
    const [news, setNews] = useState([]);
    const [email, setEmail] = useState("");
    const [subscribing, setSubscribing] = useState(false);
    const trackRef = useRef(null);
    const pausedRef = useRef(false);
    const rafRef = useRef(null);

    useEffect(() => {
        api.get("/news/latest").then((r) => setNews(r.data.items || [])).catch(() => setNews([]));
    }, []);

    useEffect(() => {
        if (!news.length) return;
        const track = trackRef.current;
        if (!track) return;
        if (!window.matchMedia("(min-width: 768px)").matches) return;
        const step = () => {
            if (!pausedRef.current && track) {
                track.scrollLeft += 0.6;
                if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 1) {
                    track.scrollLeft = 0;
                }
            }
            rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafRef.current);
    }, [news]);

    const scrollByAmount = (dir) => {
        pausedRef.current = true;
        trackRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
        setTimeout(() => { pausedRef.current = false; }, 1200);
    };

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setSubscribing(true);
        try {
            const r = await api.post("/newsletter/subscribe", { email });
            toast.success(r.data.already_subscribed ? "Sei già iscritto alla newsletter." : "Iscrizione confermata! Riceverai il riepilogo mensile.");
            setEmail("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Email non valida, riprova.");
        } finally {
            setSubscribing(false);
        }
    };

    if (!news.length) return null;

    // Lista duplicata per rendere il loop dello scroll continuo impercettibile
    const doubled = [...news, ...news];

    return (
        <section data-testid="news-radar-section" className="w-full px-6 md:px-12 lg:px-20 2xl:px-28 py-16 lg:py-20">
            <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                    <span className="news-arrow-neu w-11 h-11 flex items-center justify-center flex-shrink-0"><Newspaper size={18} /></span>
                    <div>
                        <span className="label-eyebrow">Aggiornato ogni giorno · AI</span>
                        <h2 className="font-serif text-2xl sm:text-3xl text-ink-50 mt-1">Notizie del mese</h2>
                    </div>
                </div>
                <div className="hidden md:flex gap-2">
                    <button type="button" data-testid="news-prev-btn" onClick={() => scrollByAmount(-1)} className="news-arrow-neu w-10 h-10 flex items-center justify-center"><ChevronLeft size={16} /></button>
                    <button type="button" data-testid="news-next-btn" onClick={() => scrollByAmount(1)} className="news-arrow-neu w-10 h-10 flex items-center justify-center"><ChevronRight size={16} /></button>
                </div>
            </div>

            <div
                ref={trackRef}
                data-testid="news-carousel-track"
                onMouseEnter={() => { pausedRef.current = true; }}
                onMouseLeave={() => { pausedRef.current = false; }}
                className="news-carousel-track flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory md:snap-none -mx-4 px-4 md:mx-0 md:px-0 pb-2"
            >
                {doubled.map((n, i) => (
                    <a
                        key={`${n.id}-${i}`}
                        href={n.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={i < news.length ? `news-card-${n.id}` : undefined}
                        className="news-card-neu flex-shrink-0 w-[80vw] max-w-[340px] md:w-[340px] md:max-w-none snap-start md:snap-align-none block overflow-hidden"
                    >
                        <div className="h-40 overflow-hidden">
                            <img src={n.image_url} alt={n.title} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="p-5">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-blue-600 font-medium mb-2">{n.source}</div>
                            <h3 className="font-serif text-base leading-snug mb-2 line-clamp-2">{n.title}</h3>
                            <p className="text-xs leading-relaxed line-clamp-2">{n.summary}</p>
                            <div className="flex items-center gap-1 text-xs text-blue-600 mt-3 font-medium">Leggi l&apos;articolo <ArrowUpRight size={12} /></div>
                        </div>
                    </a>
                ))}
            </div>

            <form onSubmit={subscribe} data-testid="newsletter-form" className="news-card-neu mt-10 max-w-xl mx-auto p-6 flex flex-col sm:flex-row items-center gap-4">
                <Mail size={20} className="text-blue-600 flex-shrink-0" />
                <div className="flex-1 text-center sm:text-left">
                    <div className="text-sm font-medium" style={{ color: "#1e293b" }}>Ricevi il riepilogo mensile delle news immobiliari</div>
                    <div className="text-xs mt-0.5" style={{ color: "#64748b" }}>Le migliori notizie del mese, una email al mese, disiscrizione in un click.</div>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <input
                        data-testid="newsletter-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="La tua email"
                        className="flex-1 sm:w-48 px-3 py-2 rounded-full border border-blue-100 text-sm outline-none focus:border-blue-400"
                        style={{ color: "#1e293b" }}
                    />
                    <button data-testid="newsletter-subscribe-btn" type="submit" disabled={subscribing} className="news-arrow-neu px-5 py-2 text-sm font-medium whitespace-nowrap">
                        {subscribing ? "..." : "Iscriviti"}
                    </button>
                </div>
            </form>
        </section>
    );
};

export default NewsRadar;
